import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
}                       from 'react-query';
import { AxiosError }   from 'axios';
import { callApi }      from '@utils/apiCaller';
import { Notification } from '@components/Notification/Notification';
import {
  showErrorMessage,
  parseRecords,
}                       from '@utils/commonFunctions';
import {
  IUpdateBidderPassPayload,
  IBidderUser,
  IComplaint,
  IChangeStatusPayload,
}                       from '@typings/registeredBidders';
import {
  ISearchParams,
  ISearchResult,
}                       from '@typings/search';
import { IBid }         from '@typings/auction';

// #region UPDATE BIDDER PASSWORD
const updateBidderPassword = async ({
  userId,
  newPassword,
}: IUpdateBidderPassPayload): Promise<void> => {
  try {
    await callApi(
      `intraUsers/update-password/${userId}`,
      'put',
      { newPassword },
    );

    Notification.enqueueSnackbar(
      'Bidder password updated',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUpdateBidderPassword = (
  config: UseMutationOptions<void, AxiosError, IUpdateBidderPassPayload> = {},
): UseMutationResult<void, AxiosError, IUpdateBidderPassPayload> => useMutation(
  updateBidderPassword,
  config,
);
// #endregion

// #region FETCH BIDDERS LIST
const fetchBiddersList = async (payload: ISearchParams): Promise<ISearchResult<IBidderUser>> => {
  try {
    const { data } = await callApi<ISearchResult<IBidderUser>>(
      'IntraUsers/bidders-search',
      'post',
      payload,
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchBiddersList = (
  payload : ISearchParams,
  config? : UseQueryOptions<ISearchResult<IBidderUser>, AxiosError>,
): UseQueryResult<ISearchResult<IBidderUser>, AxiosError> => useQuery(
  ['bidders-list', payload],
  () => fetchBiddersList(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH BIDDER INFO
const fetchBidderInfo = async (id: string): Promise<IBidderUser> => {
  try {
    const { data } = await callApi<IBidderUser>(`user/${id}`);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchBidderInfo = (
  id      : string,
  config? : UseQueryOptions<IBidderUser, AxiosError>,
): UseQueryResult<IBidderUser, AxiosError> => useQuery(
  ['bidder-info', id],
  () => fetchBidderInfo(id),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH BIDDER ACTIVITY
const fetchBidderActivity = async (id: string): Promise<IBid[]> => {
  try {
    const params: ISearchParams = {
      offset  : 0,
      limit   : 30,
      orderBy : 'DateCreated desc',
    };

    const { data } = await callApi<ISearchResult<IBid>>(
      `bids/${id}`,
      'post',
      params,
    );

    return parseRecords<IBid>(data);
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchBidderActivity = (
  id      : string,
  config? : UseQueryOptions<IBid[], AxiosError>,
): UseQueryResult<IBid[], AxiosError> => useQuery(
  ['bidder-activity', id],
  () => fetchBidderActivity(id),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH BIDDER COMPLAINTS
const fetchBidderComplaints = async (id: string): Promise<IComplaint[]> => {
  try {
    const { data } = await callApi<IComplaint[]>(`user/complaints/${id}`);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchBidderComplaints = (
  id      : string,
  config? : UseQueryOptions<IComplaint[], AxiosError>,
): UseQueryResult<IComplaint[], AxiosError> => useQuery(
  ['bidder-complaints', id],
  () => fetchBidderComplaints(id),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endergion

// #region CHANGE USER STATUS
const changeUserStatus = async ({ id, status }: IChangeStatusPayload): Promise<void> => {
  try {
    await callApi(
      `user/update-status/${id}`,
      'put',
      { status },
    );

    Notification.enqueueSnackbar(
      'Status has been changed.',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useChangeUserStatus = (
  config: UseMutationOptions<void, AxiosError, IChangeStatusPayload> = {},
): UseMutationResult<void, AxiosError, IChangeStatusPayload> => useMutation(
  changeUserStatus,
  config,
);
// #endregion
